import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Col, Form, Label } from "reactstrap";
import { RootState } from "../../../../ReduxToolkit/Store";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { manageUserService, getStaffByIdService } from "../../../../Services/CRMAdminServices/CRMStaffService";
import { toast } from "react-toastify";
import TextField from "../../../common/TextField";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import MobileField from "../../../common/MobileField";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { H3 } from "../../../../AbstractElements";
import { UserState } from "../FormData/CrmFormData";
import { Eye, EyeOff } from "react-feather";

const EditStaff = () => {
    const { userID } = useParams();
    const navigate = useSafeNavigate();
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState<any>(UserState);
    const [formErrors, setFormErrors] = useState<any>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { roles } = useSelector((state: RootState) => state.roles);
    const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/`;

    const roleOptions = roles.map((role: any) => ({
        value: role.roleID.toString(),
        label: role.roleName,
    }));

    // Fetch user details when component loads
    const { data: staffData, isLoading, isError } = useGetRequestQuery(
        ['getStaffById', userID],
        () => getStaffByIdService(userID),
        { enabled: !!userID }
    );

    useEffect(() => {
        if (staffData && !isError && !isLoading) {
            setFormData((prevData : any) => ({
                ...staffData?.data[0],
                confirmPassword: staffData?.data[0]?.passwordHash,
            }));
        }
    }, [staffData, isError, isLoading]);

    const handleValueChange = (name: string, value: string | Date | null) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
        setFormErrors((prevErrors: any) => ({
            ...prevErrors,
            [name]: "",
        }));
    };

    const {
        mutate: manageUser,
        isPending: isManageUserPending,
    } = useMutationRequestQuery("manageUser", manageUserService, {
        onSuccess: () => {
            toast.success("Staff updated successfully.");
            navigate("/CRMStaff");
            queryClient.invalidateQueries({ queryKey: ["getStaffs"] });
        },
        onError: () => {
            toast.error("Failed to update staff.");
        },
    });

    const handleSubmit = (e: any) => {
        e.preventDefault();

        let errors: any = {};

        // Required field validations
        if (!formData.firstName?.trim()) {
            errors.firstName = "First Name is required!";
        }
        if (!formData.lastName?.trim()) {
            errors.lastName = "Last Name is required!";
        }
        if (!formData.email?.trim()) {
            errors.email = "Email is required!";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Enter a valid email!";
        }
        if (!formData.mobile?.trim()) {
            errors.mobile = "Mobile is required!";
        }

        // Password validation only if provided
        if (formData.passwordHash) {
            if (formData.passwordHash !== formData.confirmPassword) {
                errors.confirmPassword = "Passwords do not match!";
            }
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        manageUser({ ...formData, action: "UPDATE" });
    };
    const handleFileChange = (e: any, fieldName: string) => {
        const file = e.target.files[0];

        if (file) {
            setFormData((prevData: any) => ({
                ...prevData,
                [fieldName]: file, // Store file directly
            }));
        }
    };

    return (
        <div className="px-4 py-4" style={{ minHeight: "300px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
            <Form style={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "white", borderRadius: "8px", padding: "15px", paddingLeft: "30px" }}>
                <div className="row">
                    <div className="d-flex justify-content-between align-items-center my-3">
                        <H3 className="heading-color f-w-900">Edit Staff</H3>
                    </div>
                    {/* Role field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <DropdownWithValueName
                                label="Role:"
                                name="roleID"
                                value={formData.roleID}
                                setValue={handleValueChange}
                                options={roleOptions}
                                passCol="col-10"
                                design="flex"
                            />
                        </Col>
                    </div>

                    {/* First Name field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="First Name:"
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                setValue={handleValueChange}
                                placeholder="First Name"
                                passCol="col-10"
                                design="flex"
                                maxChars={20}
                                error={formErrors.firstName}
                                errorMessage="First Name is required!"
                            />
                        </Col>
                    </div>

                    {/* Last Name field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Last Name:"
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                setValue={handleValueChange}
                                placeholder="Last Name"
                                passCol="col-10"
                                design="flex"
                                maxChars={20}
                                error={formErrors.lastName}
                                errorMessage="Last Name is required!"
                            />
                        </Col>
                    </div>

                    {/* Mobile field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <MobileField
                                label="Mobile:"
                                mobileField="mobile"
                                value={formData.mobile}
                                setValue={(field, value) => handleValueChange(field, value)}
                                placeholder="Mobile"
                                passCol="col-10"
                                design="flex"
                                maxLength={18}
                                error={formErrors.mobile}
                                errorMessage="Invalid mobile number"
                            />
                        </Col>
                    </div>
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Initials:"
                                type="text"
                                name="initials"
                                value={formData.initials}
                                setValue={handleValueChange}
                                placeholder="Initials"
                                passCol="col-10"
                                design="flex"
                                maxChars={15}
                            />
                        </Col>
                    </div>
                    {/* Email field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <TextField
                                label="Email:"
                                type="email"
                                name="email"
                                value={formData.email}
                                setValue={handleValueChange}
                                placeholder="Email"
                                passCol="col-10"
                                design="flex"
                                maxChars={50}
                                error={formErrors.email}
                                errorMessage="Email is required!"
                            />
                        </Col>
                    </div>

                    {/* Password field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <div className="position-relative" style={{ width: '100%' }}>
                                <TextField
                                    label="Password:"
                                    type={showPassword ? "text" : "password"}
                                    name="passwordHash"
                                    value={formData.passwordHash}
                                    setValue={handleValueChange}
                                    placeholder="Password"
                                    passCol="col-10"
                                    design="flex"
                                    maxChars={50}
                                />
                                <div 
                                    className="position-absolute" 
                                    style={{ 
                                        right: "15px", 
                                        top: "50%", 
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        zIndex: 1,
                                        padding: "5px"
                                    }}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff size={18} color="#666" />
                                    ) : (
                                        <Eye size={18} color="#666" />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </div>

                    {/* Confirm Password field */}
                    <div className="d-flex">
                        <Col xs={12} className="responsive-col-lead" style={{ width: '40%' }}>
                            <div className="position-relative" style={{ width: '100%' }}>
                                <TextField
                                    label="Confirm Password:"
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    setValue={handleValueChange}
                                    placeholder="Confirm Password"
                                    passCol="col-10"
                                    design="flex"
                                    maxChars={50}
                                    error={formErrors.confirmPassword}
                                    errorMessage={formErrors.confirmPassword}
                                />
                                <div 
                                    className="position-absolute" 
                                    style={{ 
                                        right: "15px", 
                                        top: "50%", 
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        zIndex: 1,
                                        padding: "5px"
                                    }}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff size={18} color="#666" />
                                    ) : (
                                        <Eye size={18} color="#666" />
                                    )}
                                </div>
                            </div>
                        </Col>
                    </div>
                    <Col className="" style={{ marginBottom: "20px" }}>
                        <Label>Profile Image</Label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, "profileImage")}
                            style={{marginLeft : '50px'}}
                        />
                        {formData.profilePhoto && (
                         <img
                         src={typeof formData.profilePhoto === "string" 
                             ? `${BASE_URL}StaffImage/${formData.profilePhoto}`
                             : URL.createObjectURL(formData.profilePhoto)      
                         }
                         alt="Profile"
                         style={{
                             width: "200px",
                             height: "200px",
                             marginTop: "10px",
                             objectFit: "cover",
                             display: "block",
                         }}
                     />
                        )}
                    </Col>
                </div>

                <div className="d-flex gap-3">
                    <Link
                        to="/CRMStaff"
                        style={{
                            color: "#0d6efd",
                            cursor: "pointer",
                            textDecoration: "none",
                            fontWeight: "500",
                        }}
                    >
                        Back to Staff |
                    </Link>

                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        disabled={isManageUserPending}
                        type="submit"
                    >
                        {isManageUserPending ? "Updating . . ." : "Update Staff"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default EditStaff;
